import { Component, OnInit, OnDestroy } from '@angular/core';
import { BasePageComponent } from '../base-page/base-page.component';
import { FileManagementService } from 'src/app/services/file-management.service';
import {
  FileMetadata,
  StorageQuota,
  FileBrowserTab,
  FileSelection,
  FileListResponse,
  FileTreeItem,
  FolderData
} from 'src/app/services/models/file-management.models';
import { Subject, takeUntil, forkJoin, Observable } from 'rxjs';
import { Store, select } from '@ngrx/store';
import { AppState } from 'src/app/services/models/application-details.model';
import { selectCurrentUserRole, selectCurrentUserEmail } from 'src/app/store/app-state.selectors';
import { AppUserType } from 'src/app/services/models/app-user.model';
import { MatDialog } from '@angular/material/dialog';
import { FolderSelectDialogComponent, FolderSelectDialogData } from './folder-select-dialog/folder-select-dialog.component';

@Component({
    selector: 'bhub-files',
    templateUrl: './files.component.html',
    styleUrls: ['./files.component.scss'],
    standalone: false
})
export class FilesComponent extends BasePageComponent implements OnInit, OnDestroy {
  // Tab and navigation state
  currentTab: 'user' | 'organization' | 'admin' = 'user';
  availableTabs: FileBrowserTab[] = [];
  currentFolderPath = '';
  breadcrumbs: { name: string; path: string }[] = [];

  // File data - now using hierarchical structure
  fileTreeItems: FileTreeItem[] = [];
  currentItems: FileTreeItem[] = []; // Flattened items for current folder
  filteredItems: FileTreeItem[] = [];
  selectedFiles: FileSelection = { files: [], isMultiple: false };

  // User role and quota
  currentUserRole: AppUserType | null = null;
  currentUserEmail: string | null = null;
  storageQuota: StorageQuota | null = null;

  // UI state
  isLoading = false;
  searchQuery = '';
  showUploadArea = false;
  showCreateFolder = false;
  newFolderName = '';

  // Upload progress tracking
  uploadProgress: { [fileName: string]: number } = {};
  uploadingFiles: Set<string> = new Set();

  // Get array of uploading file names for template iteration
  get uploadingFileNames(): string[] {
    return Array.from(this.uploadingFiles);
  }

  // Error handling
  error: string | null = null;

  // Large file threshold (10MB) - files larger than this will use chunked upload
  private readonly LARGE_FILE_THRESHOLD = 10 * 1024 * 1024; // 10MB

  private destroy$ = new Subject<void>();

  constructor(
    private fileService: FileManagementService,
    private store: Store<AppState>,
    private dialog: MatDialog
  ) {
    super();
  }

  ngOnInit(): void {
    // Subscribe to user role changes
    this.store.pipe(
      select(selectCurrentUserRole),
      takeUntil(this.destroy$)
    ).subscribe(role => {
      this.currentUserRole = role;
      this.setupTabs();
    });

    // Subscribe to user email
    this.store.pipe(
      select(selectCurrentUserEmail),
      takeUntil(this.destroy$)
    ).subscribe(email => {
      this.currentUserEmail = email;
    });

    this.initializeComponent();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeComponent(): void {
    this.isLoading = true;

    // Load storage quota
    this.fileService.getStorageQuota()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.storageQuota = result.quota;
          this.setupTabs();
          this.loadFiles();
        },
        error: (error) => {
          this.error = error.message || 'Failed to load storage quota';
          this.isLoading = false;
        }
      });
  }

  private setupTabs(): void {
    this.availableTabs = [
      {
        id: 'user',
        label: 'My Files',
        icon: 'folder',
        path: 'users'
      },
      {
        id: 'organization',
        label: 'Organization',
        icon: 'business',
        path: 'organization'
      }
    ];

    // Add admin tab only for Admin and Owner roles
    const hasAdminAccess = this.currentUserRole === AppUserType.Admin || this.currentUserRole === AppUserType.Owner;

    if (hasAdminAccess) {
      this.availableTabs.push({
        id: 'admin',
        label: 'Admin',
        icon: 'admin_panel_settings',
        path: '_admin'
      });
    }

    // Security check: If user is currently on admin tab but no longer has access, redirect to user tab
    if (!hasAdminAccess && this.currentTab === 'admin') {
      console.warn('User lost admin access, redirecting from admin tab to user tab');
      this.currentTab = 'user';
      this.currentFolderPath = '';
      this.selectedFiles = { files: [], isMultiple: false };
      // Reload files for the new tab
      this.loadFiles();
    }

    // Update the tab index after setting up tabs
    this.updateTabIndex();
  }

  loadFiles(): void {
    if (!this.currentUserRole) return;

    this.isLoading = true;
    this.error = null;

    // Map currentTab to storageArea with security check
    let storageArea: 'user' | 'admin' | 'organization';
    switch (this.currentTab) {
      case 'admin':
        // Security check: Only Admin and Owner can access admin storage
        if (this.currentUserRole !== AppUserType.Admin && this.currentUserRole !== AppUserType.Owner) {
          console.error('Unauthorized attempt to access admin storage. Redirecting to user storage.');
          this.currentTab = 'user';
          storageArea = 'user';
        } else {
          storageArea = 'admin';
        }
        break;
      case 'organization':
        storageArea = 'organization';
        break;
      default:
        storageArea = 'user';
        break;
    }

    // Always load the full category structure to maintain complete hierarchical view
    // This ensures that after operations like delete, we still have the full tree
    // and can navigate properly within folders while maintaining the complete structure
    const request = {
      storageArea: storageArea,
      folderPath: null, // Always null to get full hierarchy
      searchPrefix: this.searchQuery || null,
      pageSize: null
    };

    this.fileService.listFiles(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: FileListResponse) => {
          this.fileTreeItems = response.items;
          this.currentItems = this.getCurrentLevelItems(response.items, this.currentFolderPath);
          this.applySearch();
          // Use currentFolderPath instead of response.currentPath since we always load full hierarchy
          this.updateBreadcrumbs(this.currentFolderPath);
          this.isLoading = false;
        },
        error: (error) => {
          this.error = error.message || 'Failed to load files';
          this.isLoading = false;
          this.fileTreeItems = [];
          this.currentItems = [];
          this.filteredItems = [];
        }
      });
  }

  // Helper method to get current level items from hierarchical structure
  private getCurrentLevelItems(items: FileTreeItem[], currentPath: string): FileTreeItem[] {
    if (!currentPath || currentPath === '' || currentPath === '/') {
      // Root level - return direct children
      return items;
    }

    // Find the folder with the exact matching path
    const targetFolder = this.findFolderByPath(items, currentPath);
    return targetFolder ? targetFolder.items : [];
  }

  // Recursively find a folder by its full path
  private findFolderByPath(items: FileTreeItem[], targetPath: string): FileTreeItem | null {
    for (const item of items) {
      if (item.isFolder && item.path === targetPath) {
        return item;
      }

      // Recursively search in nested folders
      if (item.isFolder && item.items) {
        const found = this.findFolderByPath(item.items, targetPath);
        if (found) {
          return found;
        }
      }
    }

    return null;
  }

  private applySearch(): void {
    if (!this.searchQuery) {
      this.filteredItems = [...this.currentItems];
    } else {
      const query = this.searchQuery.toLowerCase();
      this.filteredItems = this.currentItems.filter(item =>
        item.name.toLowerCase().includes(query)
      );
    }
  }

  private updateBreadcrumbs(currentPath: string): void {
    this.breadcrumbs = [{ name: 'Root', path: '' }];

    if (currentPath && currentPath !== '/') {
      // Build breadcrumbs by finding each folder in the path hierarchy
      const pathSegments = this.buildPathSegments(currentPath);
      this.breadcrumbs.push(...pathSegments);
    }
  }

  // Build breadcrumb segments from the hierarchical data
  private buildPathSegments(targetPath: string): { name: string; path: string }[] {
    const segments: { name: string; path: string }[] = [];
    const pathParts = targetPath.split('/').filter(part => part);

    if (pathParts.length === 0) return segments;

    // For the full path like "users/edwardhfiedler_at_businesshubapp_com/Data"
    // We want to show just the meaningful folder names
    // Skip the storage area prefix and user identifier, show only folder structure

    let relevantParts = pathParts;

    // Skip "users/username" or similar prefixes for cleaner breadcrumbs
    if (pathParts[0] === 'users' && pathParts.length > 2) {
      relevantParts = pathParts.slice(2); // Skip "users" and username
    } else if (pathParts[0] === 'admin' || pathParts[0] === 'organization') {
      relevantParts = pathParts.slice(1); // Skip storage area prefix
    }

    // Build breadcrumbs for the relevant path parts
    let buildPath = '';
    pathParts.forEach((part, index) => {
      buildPath += (buildPath ? '/' : '') + part;

      if (index >= pathParts.length - relevantParts.length) {
        const displayName = relevantParts[index - (pathParts.length - relevantParts.length)];
        segments.push({
          name: displayName,
          path: buildPath
        });
      }
    });

    return segments;
  }

  // Event handlers
  onTabChange(tabId: 'user' | 'organization' | 'admin'): void {
    if (this.currentTab !== tabId) {
      this.currentTab = tabId;
      this.currentFolderPath = '';
      this.selectedFiles = { files: [], isMultiple: false };
      this.updateTabIndex();
      this.loadFiles();
    }
  }

  onFolderNavigate(folderPath: string): void {
    this.currentFolderPath = folderPath;
    this.selectedFiles = { files: [], isMultiple: false };

    // No need to make API call - use cached hierarchical data
    this.currentItems = this.getCurrentLevelItems(this.fileTreeItems, this.currentFolderPath);
    this.applySearch();
    this.updateBreadcrumbs(this.currentFolderPath);
  }

  onSearchChange(query: string): void {
    this.searchQuery = query;
    this.applySearch();
  }


  onFileSelect(item: FileTreeItem, isMultiple = false): void {
    // Convert FileTreeItem to FileMetadata for selection
    const fileMetadata = this.convertToFileMetadata(item);
    if (!fileMetadata) return;

    if (isMultiple) {
      const index = this.selectedFiles.files.findIndex(f => f.filePath === fileMetadata.filePath);
      if (index >= 0) {
        this.selectedFiles.files.splice(index, 1);
      } else {
        this.selectedFiles.files.push(fileMetadata);
      }
      this.selectedFiles.isMultiple = true;
    } else {
      this.selectedFiles = {
        files: [fileMetadata],
        isMultiple: false
      };
    }
  }

  convertToFileMetadata(item: FileTreeItem): FileMetadata | null {
    if (item.isFolder) {
      // Create a FileMetadata representation for folders
      return {
        fileName: item.name,
        filePath: item.path,
        contentType: 'folder',
        size: 0,
        uploadedBy: item.folderData?.createdBy || '',
        uploadedAt: item.folderData?.createdAt || '',
        isAdminFile: item.folderData?.isAdminFolder || false,
        isOrganizationFile: item.folderData?.isOrganizationFolder || false,
        customMetadata: {}
      };
    } else if (item.fileData) {
      return item.fileData;
    }
    return null;
  }

  onUploadToggle(): void {
    this.showUploadArea = !this.showUploadArea;
  }

  // Utility methods
  getFileIcon(item: FileTreeItem): string {
    // Handle folders
    if (item.isFolder) {
      return 'folder';
    }
    return this.fileService.getFileTypeIcon(item.fileData?.contentType || '');
  }

  formatFileSize(bytes: number): string {
    return this.fileService.formatFileSize(bytes);
  }

  canUploadToCurrentTab(): boolean {
    if (!this.currentUserRole) {
      return false;
    }

    switch (this.currentTab) {
      case 'user':
        // Users always have permission to upload to their personal files
        return true;
      case 'organization':
        // Only Admin and Owner can upload to organization
        return this.currentUserRole === AppUserType.Admin || this.currentUserRole === AppUserType.Owner;
      case 'admin':
        // Only Admin and Owner can upload to admin folder
        return this.currentUserRole === AppUserType.Admin || this.currentUserRole === AppUserType.Owner;
      default:
        return false;
    }
  }

  canDeleteSelected(): boolean {
    if (this.selectedFiles.files.length === 0 || !this.currentUserRole) {
      return false;
    }

    // Check if user can delete based on the files selected
    const hasUserFiles = this.selectedFiles.files.some(f => !f.isAdminFile && !f.isOrganizationFile);
    const hasOrgFiles = this.selectedFiles.files.some(f => f.isOrganizationFile);
    const hasAdminFiles = this.selectedFiles.files.some(f => f.isAdminFile);

    // Users can always delete their own files
    if (hasUserFiles && !hasOrgFiles && !hasAdminFiles) {
      return true;
    }

    // Only Admin/Owner can delete org or admin files
    const isAdminOrOwner = this.currentUserRole === AppUserType.Admin || this.currentUserRole === AppUserType.Owner;
    if ((hasOrgFiles || hasAdminFiles) && !isAdminOrOwner) {
      return false;
    }

    return isAdminOrOwner;
  }

  getStorageUsedPercentage(): number {
    if (!this.storageQuota || !this.storageQuota.hasQuota) return 0;
    return Math.round((this.storageQuota.usedBytes / this.storageQuota.maxBytes) * 100);
  }

  // Tab management
  currentTabIndex: number = 0;

  private updateTabIndex(): void {
    this.currentTabIndex = this.availableTabs.findIndex(tab => tab.id === this.currentTab);
    if (this.currentTabIndex === -1) {
      this.currentTabIndex = 0;
    }
  }

  onTabIndexChange(event: any): void {
    const selectedTab = this.availableTabs[event.index];
    if (selectedTab) {
      this.onTabChange(selectedTab.id);
    }
  }

  // File upload handling
  isDragActive = false;

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragActive = true;
  }

  onFileDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragActive = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.uploadFiles(Array.from(files));
    }
  }

  onFileSelected(event: any): void {
    const files = event.target.files;
    if (files && files.length > 0) {
      this.uploadFiles(Array.from(files));
    }
    // Reset input
    event.target.value = '';
  }

  private uploadFiles(files: File[]): void {
    if (!files || files.length === 0) return;

    this.isLoading = true;
    this.error = null;

    // Initialize upload progress for all files
    files.forEach(file => {
      this.uploadProgress[file.name] = 0;
      this.uploadingFiles.add(file.name);
    });

    const folderPath = this.currentFolderPath;

    if (this.currentTab === 'organization') {
      // Organization files - check if any are large
      const largeFiles = files.filter(f => f.size > this.LARGE_FILE_THRESHOLD);
      const smallFiles = files.filter(f => f.size <= this.LARGE_FILE_THRESHOLD);

      // Upload large files using chunked upload
      largeFiles.forEach(file => {
        this.uploadLargeFileToOrganization(file, folderPath);
      });

      // Upload small files using regular method
      if (smallFiles.length === 1) {
        this.fileService.uploadToOrganization(smallFiles[0], folderPath)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (response) => {
              this.uploadProgress[smallFiles[0].name] = 100;
              this.uploadingFiles.delete(smallFiles[0].name);
              this.checkAllUploadsComplete();
            },
            error: (error) => {
              this.error = error.message || 'Failed to upload file to organization';
              this.uploadingFiles.delete(smallFiles[0].name);
              this.checkAllUploadsComplete();
            }
          });
      } else if (smallFiles.length > 1) {
        this.uploadMultipleToOrganization(smallFiles, folderPath);
      }
    } else {
      // User and admin files
      const uploadToAdmin = this.currentTab === 'admin';
      const largeFiles = files.filter(f => f.size > this.LARGE_FILE_THRESHOLD);
      const smallFiles = files.filter(f => f.size <= this.LARGE_FILE_THRESHOLD);

      // Upload large files using chunked upload
      largeFiles.forEach(file => {
        this.uploadLargeFile(file, folderPath, uploadToAdmin);
      });

      // Upload small files using regular method
      if (smallFiles.length === 1) {
        this.fileService.uploadFile(smallFiles[0], folderPath, uploadToAdmin)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (response) => {
              this.uploadProgress[smallFiles[0].name] = 100;
              this.uploadingFiles.delete(smallFiles[0].name);
              this.checkAllUploadsComplete();
            },
            error: (error) => {
              this.error = error.message || 'Failed to upload file';
              this.uploadingFiles.delete(smallFiles[0].name);
              this.checkAllUploadsComplete();
            }
          });
      } else if (smallFiles.length > 1) {
        this.fileService.uploadMultipleFiles(smallFiles, folderPath, uploadToAdmin)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (response) => {
              smallFiles.forEach(file => {
                this.uploadProgress[file.name] = 100;
                this.uploadingFiles.delete(file.name);
              });
              if (response.errors && response.errors.length > 0) {
                this.error = `Some files failed to upload: ${response.errors.join(', ')}`;
              }
              this.checkAllUploadsComplete();
            },
            error: (error) => {
              this.error = error.message || 'Failed to upload files';
              smallFiles.forEach(file => {
                this.uploadingFiles.delete(file.name);
              });
              this.checkAllUploadsComplete();
            }
          });
      }
    }
  }

  private uploadLargeFile(file: File, folderPath?: string, uploadToAdmin = false): void {
    this.fileService.uploadLargeFile(
      file,
      folderPath,
      uploadToAdmin,
      false, // uploadToOrganization
      5 * 1024 * 1024, // 5MB chunks
      (progress) => {
        this.uploadProgress[file.name] = progress;
      }
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.uploadProgress[file.name] = 100;
          this.uploadingFiles.delete(file.name);
          this.checkAllUploadsComplete();
        },
        error: (error) => {
          console.error('Error uploading large file:', error);
          this.error = error.message || `Failed to upload ${file.name}`;
          this.uploadProgress[file.name] = 0; // Reset progress on error
          this.uploadingFiles.delete(file.name);
          this.checkAllUploadsComplete();
        }
      });
  }

  private uploadLargeFileToOrganization(file: File, folderPath?: string): void {
    // For organization files, we'll use the regular uploadLargeFile but with uploadToOrganization flag
    // Note: This assumes the backend supports organization uploads in the resumable endpoint
    // If not, you may need to add a separate method or modify the service
    this.fileService.uploadLargeFile(
      file,
      folderPath,
      false, // uploadToAdmin
      true,  // uploadToOrganization
      5 * 1024 * 1024, // 5MB chunks
      (progress) => {
        this.uploadProgress[file.name] = progress;
      }
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.uploadProgress[file.name] = 100;
          this.uploadingFiles.delete(file.name);
          this.checkAllUploadsComplete();
        },
        error: (error) => {
          console.error('Error uploading large file to organization:', error);
          this.error = error.message || `Failed to upload ${file.name}`;
          this.uploadProgress[file.name] = 0; // Reset progress on error
          this.uploadingFiles.delete(file.name);
          this.checkAllUploadsComplete();
        }
      });
  }

  private checkAllUploadsComplete(): void {
    // Check if all files have finished uploading by checking if uploadingFiles is empty
    if (this.uploadingFiles.size === 0) {
      this.isLoading = false;
      this.showUploadArea = false;
      this.uploadProgress = {};
      this.loadFiles();
    }
  }

  private uploadMultipleToOrganization(files: File[], folderPath?: string): void {
    let completedUploads = 0;
    const errors: string[] = [];

    files.forEach(file => {
      this.fileService.uploadToOrganization(file, folderPath)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            completedUploads++;
            this.checkMultipleOrganizationUploadComplete(completedUploads, files.length, errors);
          },
          error: (error) => {
            completedUploads++;
            errors.push(`${file.name}: ${error.message}`);
            this.checkMultipleOrganizationUploadComplete(completedUploads, files.length, errors);
          }
        });
    });
  }

  private checkMultipleOrganizationUploadComplete(completed: number, total: number, errors: string[]): void {
    if (completed === total) {
      this.isLoading = false;
      this.showUploadArea = false;
      if (errors.length > 0) {
        this.error = `Some files failed to upload: ${errors.join(', ')}`;
      }
      this.loadFiles();
    }
  }

  // File operations
  downloadFile(file: FileMetadata): void {
    const isAdminFile = file.isAdminFile;
    this.fileService.downloadFileDirectly(file.filePath, isAdminFile);
  }

  deleteFile(file: FileMetadata): void {
    const isFolder = file.contentType === 'folder';
    const itemType = isFolder ? 'folder' : 'file';

    if (!confirm(`Are you sure you want to delete this ${itemType} "${file.fileName}"?${isFolder ? ' This will delete all contents inside the folder.' : ''}`)) {
      return;
    }

    this.isLoading = true;
    this.error = null;

    let deleteOperation: Observable<any>;

    if (isFolder) {
      // Use folder deletion endpoint
      const fromOrganization = file.isOrganizationFile;
      const fromAdmin = file.isAdminFile;

      deleteOperation = this.fileService.deleteFolder(file.filePath, fromAdmin, fromOrganization);
    } else {
      // Use file deletion endpoint
      deleteOperation = this.fileService.deleteFile(file.filePath, file.isAdminFile);
    }

    deleteOperation
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.isLoading = false;
          // Remove from selection if it was selected
          this.selectedFiles.files = this.selectedFiles.files.filter(f => f.filePath !== file.filePath);
          // Reset to root view after delete since we load full hierarchy
          this.currentFolderPath = '';
          // Refresh file list
          this.loadFiles();
        },
        error: (error) => {
          this.error = error.message || `Failed to delete ${itemType}`;
          this.isLoading = false;
        }
      });
  }

  moveFile(file: FileMetadata): void {
    // Open folder selection dialog
    const dialogData: FolderSelectDialogData = {
      title: `Move "${file.fileName}" to...`,
      fileToMove: file.fileName,
      folders: this.fileTreeItems,
      currentPath: this.currentFolderPath,
      currentTab: this.currentTab
    };

    const dialogRef = this.dialog.open(FolderSelectDialogComponent, {
      width: '500px',
      maxHeight: '600px',
      data: dialogData
    });

    dialogRef.afterClosed().subscribe(destinationPath => {
      if (destinationPath !== undefined) {
        // User selected a destination
        const currentPath = file.filePath;
        const fileName = file.fileName;
        const fullDestinationPath = destinationPath ? `${destinationPath}/${fileName}` : fileName;

        this.isLoading = true;
        this.error = null;

        this.fileService.moveFile(
          currentPath,
          fullDestinationPath,
          file.isAdminFile,
          this.currentTab === 'admin'
        )
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            this.isLoading = false;
            // Remove from selection if it was selected
            this.selectedFiles.files = this.selectedFiles.files.filter(f => f.filePath !== file.filePath);
            // Reset to root view after move since we load full hierarchy
            this.currentFolderPath = '';
            // Refresh file list
            this.loadFiles();
          },
          error: (error) => {
            this.error = error.message || 'Failed to move file';
            this.isLoading = false;
          }
        });
      }
    });
  }

  // Check if user can delete this specific file
  canDeleteFile(file: FileMetadata): boolean {
    if (!this.currentUserRole) return false;

    // Users can always delete their own files
    if (!file.isAdminFile && !file.isOrganizationFile) {
      return true;
    }

    // Only Admin/Owner can delete org or admin files
    return this.currentUserRole === AppUserType.Admin || this.currentUserRole === AppUserType.Owner;
  }

  // Check if user can move this specific file
  canMoveFile(file: FileMetadata): boolean {
    // Don't allow moving folders for now
    if (file.contentType === 'folder') {
      return false;
    }

    return this.canDeleteFile(file); // Same permissions as delete for files
  }

  // Enhanced download with signed URL
  downloadFileEnhanced(file: FileMetadata): void {
    if (!file) return;

    this.isLoading = true;
    this.error = null;

    const fromAdmin = file.isAdminFile;
    const fromOrganization = file.isOrganizationFile;

    this.fileService.downloadFileWithSignedUrl(file.filePath, fromAdmin, fromOrganization)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isLoading = false;
          // Download started successfully
        },
        error: (error) => {
          this.error = error.message || 'Failed to download file';
          this.isLoading = false;
        }
      });
  }

  deleteSelectedFiles(): void {
    if (this.selectedFiles.files.length === 0) return;

    const fileCount = this.selectedFiles.files.length;
    const fileNames = this.selectedFiles.files.map(f => f.fileName).join(', ');

    if (!confirm(`Are you sure you want to delete ${fileCount} selected file${fileCount > 1 ? 's' : ''}?\n\n${fileNames}`)) {
      return;
    }

    this.isLoading = true;
    this.error = null;

    // Group files by admin status for bulk delete
    const adminFiles = this.selectedFiles.files.filter(f => f.isAdminFile).map(f => f.filePath);
    const userFiles = this.selectedFiles.files.filter(f => !f.isAdminFile).map(f => f.filePath);

    const deleteRequests = [];

    if (adminFiles.length > 0) {
      deleteRequests.push(this.fileService.deleteMultipleFiles(adminFiles, true));
    }
    if (userFiles.length > 0) {
      deleteRequests.push(this.fileService.deleteMultipleFiles(userFiles, false));
    }

    forkJoin(deleteRequests)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (responses) => {
          this.isLoading = false;
          this.selectedFiles = { files: [], isMultiple: false };

          // Check for any errors in the responses
          const allErrors: string[] = [];
          responses.forEach(response => {
            if (response.errors && response.errors.length > 0) {
              allErrors.push(...response.errors);
            }
          });

          if (allErrors.length > 0) {
            this.error = `Some files failed to delete: ${allErrors.join(', ')}`;
          }

          // Reset to root view after delete since we load full hierarchy
          this.currentFolderPath = '';
          // Refresh file list
          this.loadFiles();
        },
        error: (error) => {
          this.error = error.message || 'Failed to delete selected files';
          this.isLoading = false;
        }
      });
  }

  getEmptyStateIcon(): string {
    switch (this.currentTab) {
      case 'user':
        return 'folder_open';
      case 'organization':
        return 'business';
      case 'admin':
        return 'admin_panel_settings';
      default:
        return 'folder_open';
    }
  }


  isFileSelected(item: FileTreeItem): boolean {
    return this.selectedFiles.files.some(f => f.filePath === item.path);
  }

  // Folder creation methods
  onCreateFolderToggle(): void {
    this.showCreateFolder = !this.showCreateFolder;
    if (!this.showCreateFolder) {
      this.newFolderName = '';
    }
  }

  createFolder(): void {
    if (!this.newFolderName.trim()) {
      this.error = 'Folder name cannot be empty';
      return;
    }

    this.isLoading = true;
    this.error = null;

    const folderPath = this.currentFolderPath
      ? `${this.currentFolderPath}/${this.newFolderName.trim()}`
      : this.newFolderName.trim();

    const isAdmin = this.currentTab === 'admin';
    const isOrganization = this.currentTab === 'organization';

    this.fileService.createFolder(folderPath, isAdmin, isOrganization)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.isLoading = false;
          this.showCreateFolder = false;
          this.newFolderName = '';
          this.loadFiles();
        },
        error: (error) => {
          this.error = error.message || 'Failed to create folder';
          this.isLoading = false;
        }
      });
  }

  canCreateFolder(): boolean {
    return this.canUploadToCurrentTab();
  }
}
